import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import SEO from '../components/SEO';
import PageHero from '../components/PageHero';
import ContactForms from '../components/ContactForms';
import { Reveal, Chapter } from '../components/motion';
import { SITE, IMAGES, CITIES, cityPath, localBusinessSchema, breadcrumbSchema } from '../data/site';
import { Link } from 'react-router-dom';

export default function Contact() {
  return (
    <>
      <SEO
        title="Contact B.E. Holbrooks Co. | HVAC & Plumbing Service in Stanfield, NC"
        description="Contact B.E. Holbrooks Co. for HVAC, plumbing, excavation, and heating and air conditioning service in Stanfield, NC. Call 704-888-2411."
        path="/contact"
        schemas={[
          localBusinessSchema,
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Contact', path: '/contact' }]),
        ]}
      />

      <PageHero
        eyebrow="Get In Touch"
        h1Lines={['Contact', 'B.E. Holbrooks Co.']}
        sub="Call now for service, or send us a request and our office will follow up quickly. For over 60 years, Stanfield-area families have reached us the same way."
        image={IMAGES.officeStaff}
        imageAlt="B.E. Holbrooks Co. office team answering HVAC and plumbing service calls in Stanfield, NC"
        crumbs={[{ name: 'Contact' }]}
      />

      <section className="bg-white py-16 lg:py-24" data-testid="contact-section">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-12">
          <Reveal>
            <Chapter num="01" label="Reach Us" />
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">
              Call Now For Service
            </h2>
            <div className="mt-8 space-y-4">
              <a href={SITE.phoneHref} data-testid="contact-phone-card" className="flex items-center gap-4 rounded-xl border border-slate-200 p-5 transition-all hover:border-brand hover:shadow-md">
                <span className="rounded-full bg-brand p-3 text-white"><Phone className="h-5 w-5" /></span>
                <span>
                  <span className="block font-mono text-xs uppercase tracking-widest text-slate-400">Phone</span>
                  <span className="font-display text-2xl font-extrabold text-navy">{SITE.phone}</span>
                </span>
              </a>
              <a href={`mailto:${SITE.email}`} data-testid="contact-email-card" className="flex items-center gap-4 rounded-xl border border-slate-200 p-5 transition-all hover:border-brand hover:shadow-md">
                <span className="rounded-full bg-brand-soft p-3 text-brand"><Mail className="h-5 w-5" /></span>
                <span>
                  <span className="block font-mono text-xs uppercase tracking-widest text-slate-400">Email</span>
                  <span className="font-display text-lg font-bold text-navy">{SITE.email}</span>
                </span>
              </a>
              <div className="flex items-center gap-4 rounded-xl border border-slate-200 p-5" data-testid="contact-address-card">
                <span className="rounded-full bg-brand-soft p-3 text-brand"><MapPin className="h-5 w-5" /></span>
                <span>
                  <span className="block font-mono text-xs uppercase tracking-widest text-slate-400">Address</span>
                  <span className="font-display text-lg font-bold text-navy">{SITE.address}</span>
                  <span className="block text-sm text-slate-500">{SITE.cityStateZip}</span>
                </span>
              </div>
              <div className="flex items-center gap-4 rounded-xl border border-slate-200 p-5" data-testid="contact-hours-card">
                <span className="rounded-full bg-brand-soft p-3 text-brand"><Clock className="h-5 w-5" /></span>
                <span>
                  <span className="block font-mono text-xs uppercase tracking-widest text-slate-400">Store & Office</span>
                  <span className="font-display text-sm font-bold text-navy">Hardware & garden store at 204 West Stanly Street — come see us</span>
                </span>
              </div>
            </div>

            <h3 className="mt-10 font-display text-lg font-bold text-navy">Service Areas</h3>
            <p className="mt-2 flex flex-wrap gap-x-2 gap-y-1 text-sm text-slate-600" data-testid="contact-area-list">
              {CITIES.map((c, i) => (
                <span key={c.slug}>
                  <Link to={cityPath(c.slug)} className="font-medium text-brand hover:text-brand-dark">{c.name}</Link>
                  {i < CITIES.length - 1 && <span className="text-slate-300"> · </span>}
                </span>
              ))}
              <span className="text-slate-500"> & surrounding NC communities</span>
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <ContactForms />
          </Reveal>
        </div>
      </section>

      <section className="bg-brand-soft/40 py-16" data-testid="contact-map-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Reveal>
            <h2 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">Find Us in Stanfield</h2>
            <div className="mt-8 overflow-hidden rounded-2xl border border-slate-200 shadow-lg">
              <iframe
                title="Map to B.E. Holbrooks Co. at 204 West Stanly Street, Stanfield, NC 28163"
                src={`https://www.google.com/maps?q=${encodeURIComponent('204 West Stanly Street, Stanfield, NC 28163')}&output=embed`}
                className="h-96 w-full"
                loading="lazy"
                data-testid="contact-map"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
